// The shapes the awards engine reads and writes (AWARDS.md "Data" and "The
// framework"). Facts are numbers per team, matchup, and transaction; the
// host builds them from whatever it has (the simulator today, D1 and the
// live Durable Object later). No sport names, no player lookups: a player
// is an id and a name function.

export type Tone = 'glory' | 'shame' | 'fun';
export type Tier = 'bronze' | 'silver' | 'gold' | 'platinum';
export const TIERS: readonly Tier[] = ['bronze', 'silver', 'gold', 'platinum'];

/** Icon ids the UI maps to glyphs; the engine never renders. */
export type IconId =
  | 'crown'
  | 'toilet'
  | 'target'
  | 'bolt'
  | 'armchair'
  | 'magnet'
  | 'heart-broken'
  | 'dice'
  | 'star'
  | 'rocket'
  | 'trophy'
  | 'medal'
  | 'chart'
  | 'flame'
  | 'sword'
  | 'ghost'
  | 'shield'
  | 'mountain'
  | 'sparkles'
  | 'hammer'
  | 'skull'
  | 'podium'
  | 'telescope'
  | 'anchor'
  | 'briefcase'
  | 'flag'
  | 'lock';

// ── Facts ─────────────────────────────────────────────────────────────────

export interface TeamFact {
  teamId: string;
  name: string;
  ownerName: string;
}

/** One team in one period. Numbers are in the league's own scoring. */
export interface TeamPeriodFact {
  teamId: string;
  period: number;
  /** False on a bye period (odd team counts). */
  played: boolean;
  opponentTeamId: string | null;
  score: number;
  opponentScore: number | null;
  // THE LINEUP'S NUMBERS ARE NULL WHEN NOBODY WROTE THE WEEK DOWN — a real
  // league's week settled before `team_periods` existed (0022), or a
  // projection that was never made. Null is "not known", never 0: a metric
  // over a null leaves the team out rather than handing it an award for
  // leaving nothing on a bench nobody recorded.
  /** The lineup's projected total before the games. */
  projected: number | null;
  benchPoints: number | null;
  /** The best lineup the roster could have started, by actual points. */
  optimalPoints: number | null;
  topStarter: { playerId: string; points: number } | null;
  emptyStarters: number | null;
  /** Starters who were out or on bye. */
  inactiveStarters: number | null;
  result: 'win' | 'loss' | 'tie' | null;
  /** The opponent had a better record at kickoff. */
  opponentRecordBetter: boolean;
  /**
   * The week's second game, in a league that plays two opponents a week
   * (scenario Q6, B-614): the same score set against a second team. The
   * fields above are the first game's. Absent or null in a week of one game.
   */
  second?: SecondGameFact | null;
}

/** A team's second game of a week (Q6): its opponent and its result; the score is the week's. */
export interface SecondGameFact {
  opponentTeamId: string;
  opponentScore: number;
  result: 'win' | 'loss' | 'tie';
  opponentRecordBetter: boolean;
}

export interface MatchupFact {
  id: string;
  period: number;
  kind: 'regular' | 'playoff' | 'consolation';
  homeTeamId: string;
  awayTeamId: string;
  homeScore: number;
  awayScore: number;
  final: boolean;
  /**
   * Who it went to, as the scorer settled it (N1, B-232): null for a tie.
   * Absent where nothing stored one — the simulator — and the scores decide,
   * a level bracket game going to the home side, the higher seed.
   */
  winnerTeamId?: string | null;
  /** The rule that settled a level score: `bench-points`, `coin-flip`, `home-team`, `higher-seed`. */
  decidedBy?: string | null;
}

/** A player acquired during the season and what he did for the team. */
export interface TransactionFact {
  id: string;
  period: number;
  teamId: string;
  kind: 'add' | 'claim' | 'trade';
  playerId: string;
  /** What the player scored for this team in the period he was acquired. */
  pointsThisPeriod: number;
  /** What he has scored for this team from acquisition through the last final period. */
  pointsSince: number;
}

export interface PeriodFacts {
  period: number;
  kind: 'regular' | 'playoff' | 'consolation';
  final: boolean;
  teams: TeamPeriodFact[];
  matchups: MatchupFact[];
  transactions: TransactionFact[];
}

export interface StandingFact {
  teamId: string;
  rank: number;
  wins: number;
  losses: number;
  ties: number;
  pointsFor: number;
  pointsAgainst: number;
}

export interface BracketFact {
  final: boolean;
  championTeamId: string | null;
  runnerUpTeamId: string | null;
  /** The toilet bowl's last team standing, or the bottom of the standings. */
  lastPlaceTeamId: string | null;
  /** "Beat Dave's Packers 131.2–118.4 in the final." */
  finalLine: string | null;
  playoffTeamIds: string[];
  /**
   * The championship's weeks once the bracket is set: two in a league whose
   * final is played over two weeks and won on the total, so a leg lost is not
   * the end. Absent where nothing knows them (the simulator plays a one-week
   * final). Added 2026-10-19 (H9).
   */
  finalPeriods?: number[];
}

/** One team's season, as the rollover summarizes it (season_history). */
export interface PastSeasonTeam {
  teamId: string;
  ownerName: string;
  wins: number;
  losses: number;
  ties: number;
  pointsFor: number;
  pointsAgainst: number;
  finish: number;
  of: number;
  playoffs: boolean;
  champion: boolean;
  runnerUp: boolean;
  /**
   * The toilet bowl's last team standing that season (B-528); absent where
   * nothing said, when last place is the table's bottom (`finish === of`).
   */
  lastPlace?: boolean;
  /** award id → times won that season. */
  weeklyAwards: Record<string, number>;
  seasonAwards: string[];
  bestWeek: number;
  longestStreak: number;
  biggestMargin: number;
  tradesCompleted: number;
  claimsWon: number;
  perfectWeeks: number;
  ironStreak: number;
  giantKills: number;
  escapes: number;
  /** Head-to-head that season, by opponent: wins, losses, ties, and the streak at season's end (positive = wins in a row). */
  vs?: Record<string, { wins: number; losses: number; ties: number; streak: number }>;
}

export interface PastSeasonFact {
  season: number;
  teams: PastSeasonTeam[];
}

export interface SeasonFacts {
  leagueId: string;
  season: number;
  teams: TeamFact[];
  regularSeasonEnd: number;
  currentPeriod: number;
  /** Ascending, including the current period whether or not it is final. */
  periods: PeriodFacts[];
  /** Regular season, as it stands. */
  standings: StandingFact[];
  bracket: BracketFact;
  history: PastSeasonFact[];
  playerName: (playerId: string) => string;
  trophyName: string;
  /**
   * The league's format (`LeagueFormatId`): `all-play` and `points-total`
   * play weeks with no opponent, whose team facts carry no result. Absent is
   * head-to-head. Added 2026-10-16 (PLAN-2026-10-16-league-formats).
   */
  format?: string;
  /**
   * Each team's pick'em points this season, by team id (scenario I6): a
   * point a winner picked, half a tie. Absent or missing a team is no picks.
   * Added 2026-10-19.
   */
  pickem?: Readonly<Record<string, number>>;
}

// ── Definitions ───────────────────────────────────────────────────────────

export type TeamPeriodMetricId =
  | 'points'
  | 'benchPoints'
  | 'leftOnBench'
  | 'marginOfVictory'
  | 'marginOfDefeat'
  | 'pointsInLoss'
  | 'pointsInWin'
  | 'overProjection'
  | 'underProjection'
  | 'topStarterPoints'
  | 'topStarterShare';

export type MatchupMetricId = 'margin';

export type TeamSeasonMetricId =
  | 'pointsFor'
  | 'pointsAgainst'
  | 'wins'
  | 'losses'
  | 'bestWeek'
  | 'winStreak'
  | 'lossStreak'
  | 'benchPoints'
  | 'comeback'
  | 'pickupPoints'
  | 'trades'
  | 'pickemPoints';

export type Picker =
  | { over: 'team-period'; metric: TeamPeriodMetricId; best: 'max' | 'min'; min?: number }
  | { over: 'matchup'; metric: MatchupMetricId; best: 'max' | 'min' }
  | { over: 'team-season'; metric: TeamSeasonMetricId; best: 'max' | 'min'; min?: number }
  | { over: 'bracket'; slot: 'champion' | 'runner-up' | 'last-place' }
  | { over: 'transaction'; best: 'max' };

export type Unit = 'points' | 'margin' | 'count' | 'percent' | 'none';

export interface AwardDefinition {
  id: string;
  scope: 'period' | 'season';
  name: string;
  tone: Tone;
  icon: IconId;
  /** What it is, one sentence. */
  blurb: string;
  pick: Picker;
  unit: Unit;
  /** Two or three variants with placeholders; the engine picks one by hash. */
  lines: string[];
}

export type MilestoneMetricId =
  | 'wins'
  | 'bestWeek'
  | 'winStreak'
  | 'biggestMargin'
  | 'ironStreak'
  | 'perfectWeeks'
  | 'claimsWon'
  | 'trades'
  | 'giantKills'
  | 'escapes'
  | 'lowScores'
  | 'weeklyAwards'
  | 'playoffTrips'
  | 'titles';

export interface MilestoneDefinition {
  id: string;
  name: string;
  tone: Tone;
  icon: IconId;
  blurb: string;
  metric: MilestoneMetricId;
  /** Season metrics reset each season; career ones add the league's history. */
  scope: 'season' | 'career';
  unit: Unit;
  /** Ascending thresholds. */
  tiers: { tier: Tier; threshold: number }[];
  /** "Scored {value} in a week." */
  line: string;
}

export interface Catalog {
  awards: AwardDefinition[];
  milestones: MilestoneDefinition[];
}

/**
 * The league's fun rules as the engine reads them.
 *
 * `LeagueSettings.fun.leagueAwards` plus whether the milestones show. Named
 * for the league, because an award and a milestone are league-scoped and an
 * achievement is account-scoped (ACHIEVEMENTS.md §2, §17) — they were one
 * object until settings schema version 2.
 */
export interface AwardRules {
  /** Hand out this period's league awards when the period ends. */
  perPeriod: boolean;
  /** Hand out season league awards when the championship is final. */
  perSeason: boolean;
  /**
   * Show the milestones: on while the league's achievements switch is
   * (`fun.achievements.enabled`), the one switch a commissioner has for badges.
   */
  milestones: boolean;
  disabled: readonly string[];
  names: Readonly<Record<string, string>>;
}

export const DEFAULT_RULES: AwardRules = {
  perPeriod: true,
  perSeason: true,
  milestones: true,
  disabled: [],
  names: {},
};

// ── Results ───────────────────────────────────────────────────────────────

export interface Winner {
  teamId: string;
  value: number;
  /** Everything a line can name: team, owner, opponent, player, margin, share… */
  detail: Record<string, string | number>;
}

export interface AwardResult {
  awardId: string;
  scope: 'period' | 'season';
  period: number | null;
  name: string;
  blurb: string;
  icon: IconId;
  tone: Tone;
  unit: Unit;
  /** Not yet minted: the leader while the period or season is still open. */
  asItStands: boolean;
  winners: Winner[];
  /** The top five, best first, as the detail sheet lists them. */
  standings: { teamId: string; value: number }[];
  line: string;
}

/** A handed-out award. Never edited; a correction supersedes it with a new mint. */
export interface Minted extends AwardResult {
  id: string;
  at: number;
  supersededBy: string | null;
}

export interface MilestoneProgress {
  id: string;
  name: string;
  blurb: string;
  icon: IconId;
  tone: Tone;
  scope: 'season' | 'career';
  unit: Unit;
  value: number;
  /** The highest tier earned, or null. */
  tier: Tier | null;
  earnedTiers: Tier[];
  nextTier: Tier | null;
  nextThreshold: number | null;
  /** 0..1 toward the next tier (1 when every tier is earned). */
  progress: number;
  /** The line for the highest earned tier, or null. */
  earnedLine: string | null;
  tiers: { tier: Tier; threshold: number; earned: boolean }[];
}

export interface TrophyRoomData {
  teamId: string;
  career: {
    seasons: number;
    wins: number;
    losses: number;
    ties: number;
    pointsFor: number;
    titles: number;
    playoffTrips: number;
    bestWeek: { value: number; season: number; period: number | null } | null;
  };
  seasons: {
    season: number;
    isCurrent: boolean;
    /** "Champion", "Runner-up", "3rd of 12", "In progress · 3rd of 12". */
    finish: string;
    record: string;
    trophies: {
      awardId: string;
      name: string;
      icon: IconId;
      tone: Tone;
      line: string;
      champion: boolean;
    }[];
    medals: {
      awardId: string;
      name: string;
      icon: IconId;
      tone: Tone;
      count: number;
      careerCount: number;
    }[];
  }[];
  milestones: MilestoneProgress[];
}
